/**
 * Local dev site origin (scheme + host + port). Vite may use any port; preview may use 3000.
 * OAuth/session use the request Host in server/auth — this module covers absolute URLs in the UI
 * (job links, share, public assets) and SSR when window is unavailable.
 */

import { VITE_DEV } from '../env/vite-env';

export const FALLBACK_DEV_SITE_ORIGIN = "http://localhost:3000";

export function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1"
  );
}

function originFromUrlIfLoopback(url: string): string | null {
  try {
    const u = new URL(url);
    if (isLoopbackHostname(u.hostname)) {
      return u.origin;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export type GetDevSiteOriginOptions = {
  /** Vike `urlOriginal` or full request URL — preferred on the server in dev */
  requestUrl?: string | null;
};

/**
 * Origin for absolute URLs in local dev. Browser: current tab. Server: request URL, then
 * AUTH_BASE_URL if loopback, else localhost:3000.
 */
export function getDevSiteOrigin(options?: GetDevSiteOriginOptions): string {
  if (!VITE_DEV) {
    return FALLBACK_DEV_SITE_ORIGIN;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  const fromRequest = options?.requestUrl ? originFromUrlIfLoopback(options.requestUrl) : null;
  if (fromRequest) {
    return fromRequest;
  }
  const auth = typeof process !== "undefined" ? process.env.AUTH_BASE_URL?.trim() : "";
  if (auth) {
    const fromAuth = originFromUrlIfLoopback(auth);
    if (fromAuth) {
      return fromAuth;
    }
  }
  return FALLBACK_DEV_SITE_ORIGIN;
}
